/* eslint-disable */
import React from "react";
import moment from "moment";

/**
 * Chronological audit trail for a loan application — who moved it to each
 * stage, when, and why it was rejected.
 *
 * Each loan carries stage nodes (submitted/booked/authorized/initiated/paid/
 * rejected). Every node holds a Unix-ms `date` and the acting `officer`. This
 * mirrors the trail on /loans/status so the support desk answers "who rejected
 * my loan and what did they say?" with exactly what a loan officer sees.
 *
 * Kept as a component (rather than copied into each page) so the two views
 * cannot drift on the legacy-shape handling below.
 */

const TRAIL_STAGES = [
  { key: "submitted", label: "Submitted", dot: "bg-blue-500", text: "text-blue-700 dark:text-blue-400" },
  { key: "booked", label: "Booked", dot: "bg-green-500", text: "text-green-700 dark:text-green-400" },
  { key: "authorized", label: "Authorized", dot: "bg-teal-500", text: "text-teal-700 dark:text-teal-400" },
  { key: "initiated", label: "Initiated", dot: "bg-amber-500", text: "text-amber-700 dark:text-amber-400" },
  { key: "paid", label: "Paid", dot: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-400" },
  { key: "rejected", label: "Rejected", dot: "bg-red-500", text: "text-red-700 dark:text-red-400" },
];

// Schema declares these as arrays but the service writes plain objects —
// accept either shape.
const getStageNode = (value) => (Array.isArray(value) ? value[0] : value);

// Older records sometimes stored a raw Mongo ObjectId (or nothing) in place of
// the officer's name. Treat those as "unknown" so we never print an id.
const cleanOfficer = (value) => {
  if (!value) return null;
  const name = String(value).trim();
  if (!name || name === "—" || /^[0-9a-f]{24}$/i.test(name)) return null;
  return name;
};

// The DB stores Unix milliseconds. Coerce numeric strings before parsing so
// moment treats them as timestamps, and only keep genuinely valid dates.
const parseTrailDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const m = isNaN(Number(value)) ? moment(value) : moment(Number(value));
  return m.isValid() ? m : null;
};

export function buildTrail(loan) {
  return TRAIL_STAGES.map((stage) => {
    const node = getStageNode(loan?.[stage.key]);
    const date = parseTrailDate(node?.date);
    if (!date) return null; // missing/legacy/{status:false} stages drop out
    const by =
      stage.key === "submitted"
        ? `${loan?.user?.name || loan?.fullname || "Member"} (member)`
        : cleanOfficer(node?.officer);
    // Rejected carries a reason and an "edit requested" flag for the member.
    const message = stage.key === "rejected" ? node?.message : null;
    const editRequested =
      stage.key === "rejected" && (node?.editFormChecked || node?.resubmit);
    return { ...stage, date, by, message, editRequested };
  })
    .filter(Boolean)
    .sort((a, b) => a.date.valueOf() - b.date.valueOf());
}

function LoanActivityTrail({ loan }) {
  const trail = buildTrail(loan);

  if (trail.length === 0) {
    return (
      <span className="text-xs text-slate-400 dark:text-slate-500 italic">
        No activity yet
      </span>
    );
  }

  return (
    <ol className="relative">
      {trail.map((t, idx) => (
        <li key={t.key} className="relative pl-6 pb-4 last:pb-0">
          {/* connector line to the next stage */}
          {idx !== trail.length - 1 && (
            <span
              className="absolute left-[5px] top-2 -bottom-1 w-px bg-slate-200 dark:bg-gray-600"
              aria-hidden
            />
          )}
          {/* stage dot */}
          <span
            className={`absolute left-0 top-1 w-[11px] h-[11px] rounded-full ring-2 ring-white dark:ring-gray-800 ${t.dot}`}
            aria-hidden
          />
          <div className="flex flex-wrap items-baseline gap-x-2 leading-tight">
            <span className={`text-xs font-bold ${t.text}`}>{t.label}</span>
            <span className="text-[11px] text-slate-400 dark:text-slate-500 whitespace-nowrap">
              {t.date.format("MMM DD, YYYY • h:mm A")}
            </span>
          </div>

          {t.by && (
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-tight mt-0.5">
              by <span className="font-semibold">{t.by}</span>
            </p>
          )}

          {/* NOTE: this project is on Tailwind 3.1.8 — the `950` colour shades
              only exist from 3.3, so `dark:bg-red-950` compiles to nothing and
              the box keeps its near-white light-mode background in dark mode.
              Stick to shades <= 900 here. */}
          {t.message && (
            <div className="mt-1.5 px-2.5 py-1.5 bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-800 rounded-lg">
              <p className="text-[11px] text-red-800 dark:text-red-200 leading-snug">
                {t.message}
              </p>
              {t.editRequested && (
                <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-bold text-red-700 dark:text-red-300">
                  &#9998; Edit requested from member
                </span>
              )}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

export default LoanActivityTrail;
